import { Link, Navigate, useLocation } from "react-router-dom";
import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../lib/auth";
import { entitlementHasFeature, useSubscription } from "../lib/subscription";
import {
  getSetupGuideStatus,
  shouldOfferSetupGuide,
  isValidSetupGuideStatus,
  type SetupGuideStatus,
} from "../lib/setupGuide";
import {
  WEB_PLATFORM_NOTICE_TITLE,
  WEB_PLATFORM_NOTICE_BODY,
  WEB_PLATFORM_NOTICE_ACTION,
} from "../lib/webPlatformLock";

// Bereiche, die OHNE aktives Abo erreichbar bleiben (Konto & Abo-Verwaltung,
// Datenschutzerklärung & Impressum aus Transparenz-/Pflichtangaben-Gründen).
const EXEMPT_PREFIXES = ["/abo", "/einstellungen", "/datenschutz", "/impressum"];

function isExempt(pathname: string): boolean {
  return EXEMPT_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(p + "/"),
  );
}

/**
 * Sperrt die App, solange kein aktives Abo vorliegt. Ohne aktives Abo werden
 * gesperrte Routen auf die Paketauswahl umgeleitet. Zusätzlich ist der Zugang
 * zur Web-Plattform an das Feature `web_platform` gekoppelt: Nutzer mit aktivem
 * Abo, das aber keinen Web-Zugang enthält (z. B. Basic), sehen einen
 * verständlichen Upgrade-Hinweis statt eines leeren Bildschirms. Admins haben
 * immer Zugriff.
 */
export default function SubscriptionGate({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const location = useLocation();
  const { data, isLoading, isError } = useSubscription();

  // Konto-gebundener Status (geräteunabhängig) direkt aus dem Auth-Payload,
  // plus lokaler Best-Effort-Fallback. Entscheidet bereits einer davon, ist der
  // Assistent erledigt; nur wenn beide leer sind, brauchen wir die inhaltsbasierte
  // Heuristik (hasContent) vom Server. Der Hook MUSS vor den Early-Returns stehen.
  const localStatus = getSetupGuideStatus(user?.id);
  const authServerStatus = isValidSetupGuideStatus(user?.setupGuideStatus)
    ? user.setupGuideStatus
    : null;
  const undecided = authServerStatus === null && localStatus === null;
  const {
    data: setupState,
    isLoading: setupLoading,
    isError: setupError,
  } = useQuery<{
    status: SetupGuideStatus | null;
    hasContent: boolean;
  }>({
    queryKey: ["/api/setup-guide/state"],
    enabled: undecided,
  });

  if (isExempt(location.pathname)) return <>{children}</>;

  const isAdmin = !!user?.isAdmin;

  // Abo-Sperre nur für Nicht-Admins prüfen (Admins haben immer Zugriff).
  if (!isAdmin) {
    if (isLoading) {
      return (
        <div className="py-24 text-center text-sm text-[var(--color-app-text-tertiary)]">
          Abo-Status wird geprüft …
        </div>
      );
    }

    // Bei Lade-Fehler nicht aussperren — Konto/Abo bleiben ohnehin erreichbar.
    if (isError) return <>{children}</>;

    if (!data?.entitlement?.active) {
      return <Navigate to="/pakete" replace />;
    }
  }

  // Aktives Abo (oder Admin) vorhanden: Der Einrichtungs-Assistent wird erst
  // JETZT — nach der Abo-Auswahl — angeboten, sodass sein Drohnen-Schritt sofort
  // speichern kann (keine 402-Abo-Sperre mehr). Angeboten nur für echte Erstnutzer:
  // kein konto-gebundener Status (Server/lokal) und keine bereits gepflegten Daten.
  // So erscheint er geräteunabhängig nicht erneut und nicht für Bestandskonten.
  if (undecided && setupLoading) {
    return (
      <div className="py-24 text-center text-sm text-[var(--color-app-text-tertiary)]">
        Einrichtung wird geprüft …
      </div>
    );
  }
  // Bei Lade-Fehler der Einrichtungs-Status-Abfrage NICHT in den Assistenten
  // umleiten: Bestandsnutzer würden sonst bei einem vorübergehenden API-Fehler
  // fälschlich erneut in die Ersteinrichtung geschickt. Im Zweifel durchlassen.
  const serverStatus = isValidSetupGuideStatus(setupState?.status)
    ? setupState.status
    : authServerStatus;
  // Der Inhaltsstatus gilt nur als aufgelöst, wenn die Statusfrage bereits über
  // Auth/lokal entschieden ist ODER die Server-Abfrage tatsächlich Daten geliefert
  // hat. Solange `undecided` gilt und `setupState` noch fehlt (z. B. Query noch
  // nicht aufgelöst), zählt das Konto NICHT als bestätigt leer -> im Zweifel NICHT
  // anbieten (kein kurzes Aufblitzen bei Bestandskonten).
  const contentResolved = !undecided || setupState !== undefined;
  if (
    !setupError &&
    shouldOfferSetupGuide({
      serverStatus,
      localStatus,
      hasContent: setupState?.hasContent === true,
      contentResolved,
    })
  ) {
    return <Navigate to="/einrichtung" replace />;
  }

  // Aktives Abo vorhanden, aber kein Web-Plattform-Zugang -> Upgrade-Hinweis.
  if (!isAdmin && !entitlementHasFeature(data?.entitlement, "web_platform")) {
    return <WebPlatformUpgradeNotice />;
  }

  return <>{children}</>;
}

function WebPlatformUpgradeNotice() {
  return (
    <div className="mx-auto max-w-xl py-24 text-center">
      <h1 className="mb-3 text-2xl font-semibold text-[var(--color-app-text)]">
        {WEB_PLATFORM_NOTICE_TITLE}
      </h1>
      <p className="mb-6 text-sm leading-relaxed text-[var(--color-app-text-secondary)]">
        {WEB_PLATFORM_NOTICE_BODY}
      </p>
      <Link
        to="/pakete"
        className="inline-flex items-center justify-center rounded-lg bg-[var(--color-app-accent)] px-5 py-2.5 text-sm font-medium text-white"
      >
        {WEB_PLATFORM_NOTICE_ACTION}
      </Link>
    </div>
  );
}
